import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import {
  DEFAULT_EDGE_CURVATURE,
  EdgeCurvedArrowProgram,
  indexParallelEdgesIndex,
} from '@sigma/edge-curve'
import { fitViewportToNodes } from '@sigma/utils'
import { DirectedGraph, MultiGraph } from 'graphology'
import louvain from 'graphology-communities-louvain'
import { circlepack } from 'graphology-layout'
import { Sigma } from 'sigma'
import { EdgeArrowProgram } from 'sigma/rendering'

import * as state from '../../../../../../../state'

const GRAPH_SETTINGS = {
  colors: {
    dimension: {
      default: ds.color.Scale.PRIMARY.dark,
      faded: ds.color.Scale.PRIMARY.lighter,
    },
    edge: {
      highlighted: ds.color.Scale.PRIMARY.light,
    },
    entity: {
      default: ds.color.Scale.PRIMARY.darker,
      faded: ds.color.Scale.PRIMARY.lighter,
    },
  },
  renderEdgeLabels: true,
  sizes: {
    dimension: 20,
    edge: { highlighted: 4 },
    entity: 10,
  },
  weights: {
    dimensionReference: 3,
    entityToParent: 5,
  },
  zIndex: {
    edge: { default: 0, highlighted: 1 },
  },
} as const

/* ------------------- Additional node/edge attribute keys ------------------ */
const FADED_ATTR_KEY = 'faded'
const FOCUSED_ATTR_KEY = 'focused'
const HIGHLIGHTED_ATTR_KEY = 'highlighted'

const PARALLEL_EDGE_INDEX_ATTR_KEY = 'parallelIndex'
const PARALLEL_EDGE_MAX_INDEX_ATTR_KEY = 'parallelMaxIndex'
const PARALLEL_EDGE_MIX_INDEX_ATTR_KEY = 'parallelMinIndex'

const CURVED_EDGE_TYPE_ATTR_KEY = 'curved'
const STRAIGHT_EDGE_TYPE_ATTR_KEY = 'straight'

@ngCore.Component({
  imports: [
    ds.tag.Component,
    ngCommon.NgIf,
    ngCommon.TitleCasePipe,
    ds.input.select.multiple.Component,
    ds.button.Component,
    ds.illustration.emptyState.Component,
    ds.loader.Component,
  ],
  selector: 'atn-dms-schema-graph',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
// eslint-disable-next-line jsdoc/require-jsdoc
export class Component implements ngCore.AfterViewInit, ngCore.OnDestroy {
  /** @see {@link state.dms.schema.Feature} */
  public readonly schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /** {@link HTMLElement} which should contain the {@link Sigma} graph */
  private readonly _containerElementRefSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLElement>>('graph')

  /**
   * Just the {@link ngCore.ElementRef.nativeElement} from
   * {@link _containerElementRefSg}
   */
  private readonly _containerElementSg = ngCore.computed(
    (containerElementRef = this._containerElementRefSg()) =>
      containerElementRef?.nativeElement,
  )

  /**
   * Computed signal that determines whether the schema contains any dimensions.
   * Used to control whether the graph visualization should be displayed or the
   * empty state should be shown.
   */
  public readonly hasDimensionsSg = ngCore.computed(
    (schema = this.schemaFeature.selectors.schema.asSignal()) =>
      schema.dimensions.length > 0,
  )

  // eslint-disable-next-line jsdoc/require-jsdoc
  private readonly _graph = new MultiGraph({ type: 'directed' })

  // eslint-disable-next-line jsdoc/require-jsdoc
  private _renderer?: Sigma

  /**
   * (Re)builds the graph whenever the schema changes
   */
  private readonly _updateGraph = ngCore.effect(() => {
    const schema = this.schemaFeature.selectors.schema.asSignal()

    // Clear the graph in cased it was already built
    if (this._graph.nodes().length > 0) {
      this._graph.clear()
    }

    // Add dimensions nodes
    for (const dimension of schema.dimensions) {
      this._graph.addNode(dimension.id, {
        dimension: dimension.id,
        label: dimension.name,
      })
    }

    // Add entity nodes and edges to their dimensions, also edges to other
    // dimensions (ent -> dim) if an entity references another it. This has to
    // happen before all the dimensions nodes are added so that we can reference
    // them while adding edges
    for (const entity of schema.entities) {
      const dimensionId = ents.core.dms.models.Dimension.Id.from(entity.id)
      // Add entity node (it's initially hidden, only shown when the parent
      // dimension is focused)
      this._graph.addNode(entity.id, {
        dimension: dimensionId,
        hidden: true,
        label: `(${entity.level}) ${entity.name}`,
      })
      // Add edge to parent dimension
      this._graph.addEdge(entity.id, dimensionId, {
        weight: GRAPH_SETTINGS.weights.entityToParent,
      })
      // Iterate over attributes to find references to other dimensions and add
      // edges accordingly
      for (const attribute of schema.getAttributesOfEntity(entity.id)) {
        if (
          ents.core.dms.data.Value.Type.Reference.isReference(attribute.type) &&
          this._graph.hasNode(attribute.type.dimensionId)
        ) {
          // Add edge to referenced dimension (dim -> dim)
          this._graph.addEdge(dimensionId, attribute.type.dimensionId, {
            label: attribute.name,
            weight: GRAPH_SETTINGS.weights.dimensionReference,
          })

          // Add edge from entity to referenced dimension (ent -> dim),
          // initially hidden.
          // NOTE: - There can be multiple edges pointing to the same dimension
          this._graph.addEdge(entity.id, attribute.type.dimensionId, {
            hidden: true,
            label: attribute.name,
            weight: GRAPH_SETTINGS.weights.dimensionReference,
          })
        }
      }
    }

    // Index all parallel edges in the graph and assign attributes to track
    // their positions. This function analyzes the graph and identifies groups
    // of multiple edges between the same node pairs
    indexParallelEdgesIndex(this._graph, {
      edgeIndexAttribute: PARALLEL_EDGE_INDEX_ATTR_KEY, // Edge's index within its parallel group (0, 1, 2, etc.)
      edgeMaxIndexAttribute: PARALLEL_EDGE_MAX_INDEX_ATTR_KEY, // The highest index in each parallel group
      edgeMinIndexAttribute: PARALLEL_EDGE_MIX_INDEX_ATTR_KEY, // The lowest index in each parallel group
    })

    // Iterate through all edges to apply styling based on their parallel edge
    // grouping
    this._graph.forEachEdge(
      (
        edge,
        {
          [PARALLEL_EDGE_INDEX_ATTR_KEY]: parallelIndex,
          [PARALLEL_EDGE_MAX_INDEX_ATTR_KEY]: parallelMaxIndex,
          [PARALLEL_EDGE_MIX_INDEX_ATTR_KEY]: parallelMinIndex,
        },
      ) => {
        // Edge has both min and parallel index (part of a multi-edge group)
        if (parallelMinIndex) {
          this._graph.mergeEdgeAttributes(edge, {
            curvature: getCurvature(
              Number(parallelIndex),
              Number(parallelMaxIndex),
            ),
            type: parallelIndex
              ? CURVED_EDGE_TYPE_ATTR_KEY
              : STRAIGHT_EDGE_TYPE_ATTR_KEY,
          })
        }
        // Edge has parallel index but no min index (part of a multi-edge group
        // with exactly 2 edges)
        else if (parallelIndex) {
          this._graph.mergeEdgeAttributes(edge, {
            curvature: getCurvature(
              Number(parallelIndex),
              Number(parallelMaxIndex),
            ),
            type: CURVED_EDGE_TYPE_ATTR_KEY,
          })
        }
        // Single edge with no parallels
        else {
          this._graph.setEdgeAttribute(
            edge,
            'type',
            STRAIGHT_EDGE_TYPE_ATTR_KEY,
          )
        }
      },
    )

    // Calculate degree for each node (used while applying circlepack layout
    // later)
    for (const node of this._graph.nodes()) {
      this._graph.setNodeAttribute(node, 'degree', this._graph.degree(node))
    }

    /**
     * Run
     * {@link https://graphology.github.io/standard-library/communities-louvain.html | luvain}
     * for community detection, the graph has to be {@link DirectedGraph} for
     * this to works
     */
    louvain.assign(this._graph)

    /** Apply {@link https://graphology.github.io/standard-library/layout.html#circlepack | circlepack} layout */
    circlepack.assign(this._graph, {
      hierarchyAttributes: ['dimension', 'community', 'degree'],
    })
  })

  // eslint-disable-next-line jsdoc/require-jsdoc
  ngAfterViewInit(): void {
    this._initRenderer()
  }

  // eslint-disable-next-line jsdoc/require-jsdoc
  ngOnDestroy(): void {
    this._renderer?.kill()
  }

  /**
   * Reloads the schema data for the current zone context. This method is
   * triggered from the empty state view when no dimensions are found in the
   * graph.
   * It dispatches a loadSchema action to refresh the data and rebuild the graph
   * visualization.
   */
  public refreshSchema(): void {
    const context = this._meFeature.contexts.selectors.zoneContext.asSignal()
    if (!context) {
      return
    }
    this.schemaFeature.actions.loadSchema
      .from('DmsSchemaGraph.Refresh.Button.Clicked')
      .dispatch({
        context,
      })
  }
  /**
   * Initializes the {@link Sigma} renderer and sets up listeners for
   * interactivity (hover, double-click)
   */
  private _initRenderer(): void {
    const containerElement = this._containerElementSg()
    if (containerElement) {
      // This automatically renders the graph
      this._renderer = new Sigma(this._graph, containerElement, {
        edgeProgramClasses: {
          [CURVED_EDGE_TYPE_ATTR_KEY]: EdgeCurvedArrowProgram,
          [STRAIGHT_EDGE_TYPE_ATTR_KEY]: EdgeArrowProgram,
        },
        // Reducers are used to change the attributes of nodes/edges before they
        // are passed to the renderer, based on custom attributes we set on
        // nodes/edges
        edgeReducer: (edge, attributes) => {
          const isHighlighted = Boolean(attributes[HIGHLIGHTED_ATTR_KEY])
          return {
            ...attributes,
            color: isHighlighted
              ? GRAPH_SETTINGS.colors.edge[HIGHLIGHTED_ATTR_KEY]
              : undefined,
            label:
              isHighlighted && !!attributes.label
                ? String(attributes.label)
                : undefined,
            size: isHighlighted
              ? GRAPH_SETTINGS.sizes.edge[HIGHLIGHTED_ATTR_KEY]
              : undefined,
            zIndex: isHighlighted
              ? GRAPH_SETTINGS.zIndex.edge.highlighted
              : GRAPH_SETTINGS.zIndex.edge.default,
          }
        },
        nodeReducer: (node, attributes) => {
          attributes.x ??= Math.random()
          attributes.y ??= Math.random()
          const isFaded = Boolean(attributes[FADED_ATTR_KEY])
          if (ents.core.dms.models.Dimension.Id.isId(node)) {
            return {
              ...attributes,
              color: isFaded
                ? GRAPH_SETTINGS.colors.dimension[FADED_ATTR_KEY]
                : GRAPH_SETTINGS.colors.dimension.default,
              label:
                isFaded || !attributes.label
                  ? undefined
                  : String(attributes.label),
              size: GRAPH_SETTINGS.sizes.dimension,
            }
          }
          if (ents.core.dms.models.Entity.Id.isId(node)) {
            return {
              ...attributes,
              color: isFaded
                ? GRAPH_SETTINGS.colors.entity[FADED_ATTR_KEY]
                : GRAPH_SETTINGS.colors.entity.default,
              label:
                isFaded || !attributes.label
                  ? undefined
                  : String(attributes.label),
              size: GRAPH_SETTINGS.sizes.entity,
            }
          }
          return attributes
        },
        renderEdgeLabels: GRAPH_SETTINGS.renderEdgeLabels,
        zIndex: true,
      })

      /* -------------------------------- Listeners ------------------------------- */
      this._renderer.addListener(
        'clickNode',
        // eslint-disable-next-line @typescript-eslint/unbound-method
        ({ preventSigmaDefault, node }) => {
          preventSigmaDefault()
          this._clickNodeHandler(node)
        },
      )
      this._renderer.addListener(
        'doubleClickNode',
        // eslint-disable-next-line @typescript-eslint/unbound-method
        ({ preventSigmaDefault, node }) => {
          preventSigmaDefault()
          this._doubleClickNode(node)
        },
      )
      this._renderer.addListener('enterNode', ({ node }) =>
        this._enterNodeHandler(node),
      )
      this._renderer.addListener('leaveNode', ({ node }) =>
        this._leaveNodeHandler(node),
      )
      this._renderer.addListener(
        'doubleClickStage',
        // eslint-disable-next-line @typescript-eslint/unbound-method
        ({ preventSigmaDefault }) => {
          preventSigmaDefault()
          this._doubleClickStageHandler()
        },
      )
    }
  }

  /**
   * When a node is clicked, toggle the focused state of the node and show or
   * hide all entities of the dimension (if a dimension node is clicked) and
   * show/hide edges to referenced dimensions accordingly.
   */
  private _clickNodeHandler(node: string): void {
    if (ents.core.dms.models.Dimension.Id.isId(node)) {
      const schema = this.schemaFeature.selectors.schema.asSignal()
      const isNodeFocused = !!this._graph.getNodeAttribute(
        node,
        FOCUSED_ATTR_KEY,
      )
      // Toggle focused state of the double-clicked node
      this._graph.setNodeAttribute(node, FOCUSED_ATTR_KEY, !isNodeFocused)

      // Show/hide all entities of the focused dimension
      for (const entity of schema.getEntitiesInDimension(node)) {
        if (this._graph.hasNode(entity.id)) {
          // Toggle hidden state of the entity nodes
          this._graph.setNodeAttribute(entity.id, 'hidden', isNodeFocused)
        }
        // Iterate over attributes to find references to other dimensions, only
        // one edge per reference dimension should be visible at a time, (dim ->
        // dim or ent -> dim), initially only the dim -> dim edge is visible
        for (const attribute of schema.getAttributesOfEntity(entity.id)) {
          if (
            ents.core.dms.data.Value.Type.Reference.isReference(attribute.type)
          ) {
            const referencedDimensionId = attribute.type.dimensionId

            // Toggle hidden state of the edges from/to the referenced
            // dimensions (dim -> dim)
            const dimToDimEdges = this._graph.edges(node, referencedDimensionId)
            for (const edge of dimToDimEdges) {
              this._graph.setEdgeAttribute(edge, 'hidden', !isNodeFocused)
            }

            // Toggle hidden state of the edges from/to the referenced
            // dimensions (ent -> dim)
            const entToDimEdges = this._graph.edges(
              entity.id,
              referencedDimensionId,
            )
            for (const edge of entToDimEdges) {
              this._graph.setEdgeAttribute(edge, 'hidden', isNodeFocused)
            }
          }
        }
      }
    }
  }

  /**
   * Fit all the double-clicked node with it's neighbors to the viewport
   */
  private _doubleClickNode(node: string): void {
    if (this._renderer) {
      // Fit viewport to the node (dimension/entity) and its neighbors
      void fitViewportToNodes(
        this._renderer,
        [node, ...this._graph.neighbors(node)],
        {
          animate: true,
        },
      )
    }
  }

  /**
   * When the mouse enters a node, fade all non-neighboring nodes and highlight
   * all edges connected to the node
   */
  private _enterNodeHandler(node: string): void {
    const isDimension = ents.core.dms.models.Dimension.Id.isId(node)
    if (isDimension && this._renderer) {
      this._renderer.getContainer().style.cursor = 'pointer'
    }
    for (const neighbor of this._graph.nodes()) {
      if (!this._graph.areNeighbors(node, neighbor) && neighbor !== node) {
        this._graph.setNodeAttribute(neighbor, FADED_ATTR_KEY, true)
        continue
      }

      const edges = [
        ...this._graph.edges(node, neighbor),
        ...this._graph.edges(neighbor, node),
      ]

      for (const edge of edges) {
        this._graph.setEdgeAttribute(edge, HIGHLIGHTED_ATTR_KEY, true)
      }
    }
  }

  /**
   * Revert the changes made in {@link _enterNodeHandler} when the mouse leaves
   * the node; unfade all nodes and unhighlight all edges
   */
  private _leaveNodeHandler(node: string): void {
    const isDimension = ents.core.dms.models.Dimension.Id.isId(node)
    if (isDimension && this._renderer) {
      this._renderer.getContainer().style.cursor = 'default'
    }
    for (const neighbor of this._graph.nodes()) {
      if (!this._graph.areNeighbors(node, neighbor) && neighbor !== node) {
        this._graph.setNodeAttribute(neighbor, FADED_ATTR_KEY, false)
        continue
      }

      const edges = [
        ...this._graph.edges(node, neighbor),
        ...this._graph.edges(neighbor, node),
      ]

      for (const edge of edges) {
        this._graph.setEdgeAttribute(edge, HIGHLIGHTED_ATTR_KEY, false)
      }
    }
  }

  /**
   * Reset the viewport to show the entire graph
   */
  private _doubleClickStageHandler(): void {
    if (this._renderer) {
      void fitViewportToNodes(this._renderer, this._graph.nodes(), {
        animate: true,
      })
    }
  }
}

/**
 * Calculates the curvature value for parallel edges to prevent visual overlap.
 * It uses an exponential scaling approach to distribute edges evenly around
 * straight line.
 *
 * @see
 * {@link https://www.sigmajs.org/storybook/?path=/story/sigma-edge-curve--parallel-edges}
 */
function getCurvature(index: number, maxIndex: number): number {
  if (maxIndex <= 0) {
    throw new Error('Invalid maxIndex')
  }
  if (index < 0) {
    return -getCurvature(-index, maxIndex)
  }
  const amplitude = 3.5
  const maxCurvature =
    amplitude * (1 - Math.exp(-maxIndex / amplitude)) * DEFAULT_EDGE_CURVATURE
  return (maxCurvature * index) / maxIndex
}
